import { ArrowLeftOutlined } from '@ant-design/icons'
import {
  Alert,
  Button,
  Col,
  Descriptions,
  Divider,
  Modal,
  Progress,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd'
import { useState } from 'react'
import VSpace from '../../common/VSpace'
import { LaunchResult, LongRunningTaskFailure, LongRunningTaskRunDetails } from './longRunningTasksApi'
import {
  canCancel,
  canResume,
  formatDateTime,
  formatProgress,
  getProgressAppearance,
  getProgressPercent,
  getTaskLoadErrorMessage,
  isTaskActive,
  RefreshSetting,
  resolveRefreshInterval,
  runOutcomeColor,
  taskAutoRefreshInterval,
  taskStatusColor,
} from './longRunningTasksModel'
import { useCancelTaskMutation, useLongRunningTaskQuery, useResumeTaskMutation } from './longRunningTasksQueries'
import { getTaskTypeLabel } from './taskTypesModel'
import RefreshControl from './RefreshControl'

const { Paragraph, Text, Title } = Typography

type Props = {
  taskId: number
  launchResult: LaunchResult | null
  onLaunchResult: (result: LaunchResult | null) => void
  onBack: () => void
}

const LongRunningTaskDetail = ({ taskId, launchResult, onLaunchResult, onBack }: Props) => {
  const [modal, modalContextHolder] = Modal.useModal()
  const [refreshSetting, setRefreshSetting] = useState<RefreshSetting>('auto')
  const { data, isLoading, isFetching, dataUpdatedAt, error, refetch } = useLongRunningTaskQuery(
    taskId,
    latest => resolveRefreshInterval(refreshSetting, taskAutoRefreshInterval(latest, Date.now())),
  )
  const cancelMutation = useCancelTaskMutation()
  const resumeMutation = useResumeTaskMutation()

  const backButton = (
    <Button type="link" icon={<ArrowLeftOutlined />} onClick={onBack} style={{ paddingLeft: 0 }}>
      All tasks
    </Button>
  )

  if (isLoading) {
    return (
      <VSpace>
        {backButton}
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      </VSpace>
    )
  }

  if (!data) {
    return (
      <VSpace>
        {backButton}
        <Alert type="error" showIcon message={getTaskLoadErrorMessage(error)} />
      </VSpace>
    )
  }

  const { summary, runs, failures } = data
  const { task } = summary
  const percent = getProgressPercent(task)

  const handleCancel = () => {
    modal.confirm({
      title: `Cancel task ${task.id}?`,
      content: 'A cancelled task can be resumed later.',
      okText: 'Cancel task',
      okButtonProps: { danger: true },
      cancelText: 'Keep running',
      onOk: () => {
        cancelMutation.mutate(task.id)
      },
    })
  }

  const handleResume = () => {
    modal.confirm({
      title: `Resume task ${task.id}?`,
      content: 'A new run will be launched for this task.',
      okText: 'Resume',
      cancelText: 'Cancel',
      onOk: () => {
        resumeMutation.mutate(task.id, { onSuccess: onLaunchResult })
      },
    })
  }

  const runColumns = [
    { title: 'Run ID', key: 'id', width: 80, render: (_: unknown, details: LongRunningTaskRunDetails) => details.run.id },
    {
      title: 'Outcome',
      key: 'outcome',
      render: (_: unknown, details: LongRunningTaskRunDetails) => (
        <Tag color={runOutcomeColor(details.run.outcome)}>{details.run.outcome}</Tag>
      ),
    },
    { title: 'Version', key: 'version', render: (_: unknown, details: LongRunningTaskRunDetails) => details.run.version ?? '—' },
    {
      title: 'Launched',
      key: 'launched',
      render: (_: unknown, details: LongRunningTaskRunDetails) => (
        <div>
          <div>{formatDateTime(details.run.launchedAt)}</div>
          <Text type="secondary">{details.launchedByEmail ?? '—'}</Text>
        </div>
      ),
    },
    { title: 'Started', key: 'started', render: (_: unknown, details: LongRunningTaskRunDetails) => formatDateTime(details.run.startedAt) },
    { title: 'Ended', key: 'ended', render: (_: unknown, details: LongRunningTaskRunDetails) => formatDateTime(details.run.endedAt) },
    { title: 'Exit code', key: 'exitCode', width: 90, render: (_: unknown, details: LongRunningTaskRunDetails) => details.run.exitCode ?? '—' },
    {
      title: 'Stopped reason',
      key: 'stoppedReason',
      ellipsis: true,
      render: (_: unknown, details: LongRunningTaskRunDetails) => details.run.stoppedReason ?? '—',
    },
    {
      title: 'ECS task',
      key: 'ecsTaskArn',
      ellipsis: true,
      render: (_: unknown, details: LongRunningTaskRunDetails) => details.run.ecsTaskArn
        ? <Text copyable ellipsis style={{ maxWidth: 200 }}>{details.run.ecsTaskArn}</Text>
        : '—',
    },
  ]

  const failureColumns = [
    { title: 'Item', dataIndex: 'itemKey', width: 160 },
    { title: 'Error', dataIndex: 'error' },
    { title: 'Run', dataIndex: 'runId', width: 70 },
    {
      title: 'Failed',
      dataIndex: 'failedAt',
      width: 220,
      render: (value: string) => formatDateTime(value),
    },
  ]

  return (
    <VSpace size={16}>
      {modalContextHolder}
      {backButton}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Space size={12} align="center">
          <Title level={4} style={{ marginBottom: 0 }}>
            Task {task.id}: {getTaskTypeLabel(task.taskType)}
          </Title>
          <Tag color={taskStatusColor(task.status)}>{task.status}</Tag>
        </Space>
        <Space>
          <RefreshControl
            setting={refreshSetting}
            autoInterval={taskAutoRefreshInterval(data, Date.now())}
            isFetching={isFetching}
            dataUpdatedAt={dataUpdatedAt}
            onSettingChange={setRefreshSetting}
            onRefresh={() => refetch()}
          />
          {isTaskActive(task.status) && (
            <Button danger onClick={handleCancel} disabled={!canCancel(task)} loading={cancelMutation.isLoading}>
              Cancel task
            </Button>
          )}
          {canResume(task) && (
            <Button type="primary" onClick={handleResume} loading={resumeMutation.isLoading}>
              Resume task
            </Button>
          )}
        </Space>
      </div>

      {task.cancelRequestedAt !== null && (
        <Alert
          type="info"
          showIcon
          message={`Cancel requested by ${summary.cancelRequestedByEmail ?? 'unknown'} at ${formatDateTime(task.cancelRequestedAt)}`}
        />
      )}

      {launchResult?.localCommand && (
        <Alert
          type="info"
          showIcon
          closable
          onClose={() => onLaunchResult({ ...launchResult, localCommand: null })}
          message="Run this command locally to start the worker"
          description={<Paragraph code copyable style={{ marginBottom: 0 }}>{launchResult.localCommand}</Paragraph>}
        />
      )}

      {launchResult?.launchError && (
        <Alert
          type="warning"
          showIcon
          closable
          onClose={() => onLaunchResult({ ...launchResult, launchError: null })}
          message="The worker failed to launch"
          description={launchResult.launchError}
        />
      )}

      {summary.isStalled && (
        <Alert type="warning" showIcon message="This task is stalled." />
      )}

      {summary.isHeartbeatStale && (
        <Alert type="warning" showIcon message="This task's heartbeat is stale." />
      )}

      {task.statusDetail && <Alert type="info" showIcon message={task.statusDetail} />}

      {task.lastError && (
        <Alert
          type="error"
          showIcon
          message="Last error"
          description={<Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{task.lastError}</Paragraph>}
        />
      )}

      <Row gutter={16}>
        <Col span={12}>
          <Statistic title="Processed" value={formatProgress(task)} />
          {percent !== null && (
            <Progress percent={percent} size="small" {...getProgressAppearance(task.status)} />
          )}
        </Col>
        <Col span={12}>
          <Statistic
            title="Failed"
            value={task.failedCount}
            valueStyle={task.failedCount > 0 ? { color: '#cf1322' } : undefined}
          />
        </Col>
      </Row>

      <Descriptions column={2} size="small" bordered>
        <Descriptions.Item label="Parameters" span={2}>
          <Text code>{task.parameters}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Created">
          {formatDateTime(task.createdAt)}
          <div><Text type="secondary">{summary.createdByEmail ?? '—'}</Text></div>
        </Descriptions.Item>
        <Descriptions.Item label="Updated">{formatDateTime(task.updatedAt)}</Descriptions.Item>
        <Descriptions.Item label="Started">{formatDateTime(task.startedAt)}</Descriptions.Item>
        <Descriptions.Item label="Completed">{formatDateTime(task.completedAt)}</Descriptions.Item>
        <Descriptions.Item label="Heartbeat">{formatDateTime(task.heartbeatAt)}</Descriptions.Item>
        <Descriptions.Item label="Last progress">{formatDateTime(task.lastProgressAt)}</Descriptions.Item>
        <Descriptions.Item label="Current run ID">{task.currentRunId ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Resume cursor">
          {task.resumeCursor ? <Text code>{task.resumeCursor}</Text> : '—'}
        </Descriptions.Item>
      </Descriptions>

      <Divider orientation="left" style={{ margin: 0 }}>Runs</Divider>
      <Table<LongRunningTaskRunDetails>
        dataSource={runs}
        rowKey={details => details.run.id}
        columns={runColumns}
        size="small"
        pagination={false}
        locale={{ emptyText: 'No runs yet' }}
      />

      <Divider orientation="left" style={{ margin: 0 }}>Failures ({failures.length})</Divider>
      <Table<LongRunningTaskFailure>
        dataSource={failures}
        rowKey="id"
        columns={failureColumns}
        size="small"
        pagination={{ pageSize: 20, hideOnSinglePage: true, showSizeChanger: false }}
        locale={{ emptyText: 'No failures' }}
      />
    </VSpace>
  )
}

export default LongRunningTaskDetail
